import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { type ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  type Task,
  TasksService,
  type TaskListQuery,
  type TaskListResponse,
} from 'src/api/generated';

interface UseTasksParams extends TaskListQuery {
  enabled?: boolean;
}

interface UseTasksResponse extends ApiResponse {
  tasks: Task[];
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useTasks = (params: UseTasksParams = {}): UseTasksResponse => {
  const { enabled = true, ...queryParams } = params;

  const fetchTasks = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<TaskListResponse> => {
    const {
      limit,
      status,
      dueBefore,
      dueAfter,
      conversationId,
      assignedToType,
      assignedToId,
    } = queryParams;

    return await TasksService.getTasks(
      limit,
      pageParam,
      dueBefore,
      dueAfter,
      conversationId,
      assignedToType,
      assignedToId,
      status
    );
  };

  const getNextPageParam = (lastPage: TaskListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['tasks', queryParams],
    queryFn: fetchTasks,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const tasks = useMemoizedArrayData<Task>(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    tasks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

interface UseTaskParams {
  taskId: string;
  enabled?: boolean;
}

interface UseTaskResponse extends ApiResponse {
  task: Task | null;
}

export const useTask = ({
  taskId,
  enabled = true,
}: UseTaskParams): UseTaskResponse => {
  const fetchTask = async (): Promise<Task | null> => {
    return await TasksService.getTask(taskId);
  };

  const {
    data: task,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['tasks', taskId],
    queryFn: fetchTask,
    enabled: enabled && !!taskId,
  });

  return {
    task: task ?? null,
    loading,
    error,
    refetch,
  };
};
